import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MediaSelectionService } from '../../services/media-selection.service';
import { DialogService, LoaderService, ToastService } from '@mtgame/ui';
import {
  componentDestroyed,
  Game,
  GameMediaFilters, generateArray, handleError, MediaApi,
  MediaItem,
  Playoff, Sport,
  Tournament, TournamentApi,
  TournamentStage,
  TournamentTeam,
  TournamentTypes,
  UntilDestroy
} from '@mtgame/core';
import { getTourTitle } from '@utils/hardcode';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-media',
  templateUrl: './tournament-media.component.html',
  styleUrls: ['./tournament-media.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentMediaComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  loading: boolean;
  @Input()
  games: Game[] = [];
  @Input()
  teams: TournamentTeam[] = [];
  @Input()
  stages: TournamentStage[] = [];
  @Input()
  playoffs: Playoff[] = [];
  @Input()
  tournament: Tournament;
  @Input()
  tournaments: Tournament[];
  @Input()
  headerStickyTop: number;
  @Input()
  mediaRoute: string;
  @Input()
  sport: Sport;
  @Output()
  filterChanged = new EventEmitter<GameMediaFilters>();
  @Output()
  nextPage = new EventEmitter<void>();

  selectedImages: MediaItem[] = [];
  selectEnabled: boolean;
  mobileFiltersVisible: boolean;
  form = new UntypedFormGroup({
    tournament: new UntypedFormControl(),
    tournamentTour: new UntypedFormControl(),
    tournamentStageId: new UntypedFormControl(),
    teamId: new UntypedFormControl()
  });
  toursOptions: {value: any, title: string}[] = [];
  playoffOptions: {title: string, stages: {value: any, title: string}[]}[] = [];
  tournamentTypes = TournamentTypes;

  get selectedTournament(): Tournament {
    if (this.tournament) {
      return this.tournament;
    }
    return this.form.get('tournament').value;
  }

  constructor(
    private mediaSelectionService: MediaSelectionService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private mediaApi: MediaApi,
    private tournamentApi: TournamentApi,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tournament || changes.teams) {
      this.getTourOptions();
    }
    if (changes.playoffs) {
      this.getPlayoffOptions();
    }
  }

  getTourOptions(): void {
    if (!this.selectedTournament || !this.teams) {
      return;
    }
    if (this.teams.length > 0) {
      const tours = (this.teams.length - 1) * this.selectedTournament.settings.toursCount;
      this.toursOptions = generateArray(tours).map(tour => ({
        title: getTourTitle(tour, this.sport),
        value: {tournamentTour: tour}
      }));
    } else {
      this.toursOptions = [];
    }
    this.cdr.markForCheck();
  }

  getPlayoffOptions(): void {
    this.playoffOptions = this.playoffs.map(playoff => ({
      title: playoff.name,
      stages: playoff.getPlayoffStages().map(stage => ({
        value: {playoffStage: stage.value, playoffId: playoff.id},
        title: stage.title
      }))
    }));
  }

  ngOnInit(): void {
    this.mediaSelectionService.selected$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(selected => {
        this.selectedImages = selected;
        this.cdr.markForCheck();
      });
    this.mediaSelectionService.enabled$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(enabled => {
        this.selectEnabled = enabled;
        this.cdr.markForCheck();
      });
    this.form.valueChanges.subscribe(() => {
      const value = this.form.value;
      const tour = value.tournamentTour || {};
      delete value.tournamentTour;
      this.filterChanged.emit({
        ...tour,
        ...value,
      });
    });

    this.form.get('tournament').valueChanges.subscribe(tournament => {
      if (!tournament) {
        this.teams = [];
        this.playoffs = [];
        return;
      }
      this.loaderService.show();
      Promise.all([
        this.tournamentApi.getTeams(tournament.id),
        this.tournamentApi.getPlayoff(tournament.id),
      ]).then(([teams, playoffs]) => {
        this.teams = teams;
        this.playoffs = playoffs;
        this.getTourOptions();
      }).catch(err => {
        handleError(err);
        this.toastService.danger('Ошибка загрузки данных турнира');
      }).finally(() => {
        this.loaderService.hide();
      });
    });
  }

  ngOnDestroy(): void {
  }

  enableSelectMode() {
    this.mediaSelectionService.enable();
  }

  disableSelectMode() {
    this.mediaSelectionService.disable();
  }

  async download() {
    const selectedImages = this.selectedImages.filter(item => !item.videoUrl).map(item => item.id);
    if (selectedImages.length === 0) {
      this.toastService.danger('Выберите фотографии');
      return;
    }
    this.loaderService.show();
    try {
      const response = await this.mediaApi.createZip(selectedImages);
      const fileUrl = URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = `media.zip`;
      a.click();

      this.disableSelectMode();
      this.cdr.markForCheck();
    } catch (err) {
      handleError(err);
      this.toastService.danger('Ошибка создания архива');
    } finally {
      this.loaderService.hide();
    }
  }

  downloadImage(item: MediaItem): void {
    const link = document.createElement('a');
    link.setAttribute('href', item.originalPhoto.path);
    link.setAttribute('download', item.originalPhoto.name);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  showMobileFilters() {
    this.mobileFiltersVisible = true;
    document.body.classList.add('no-scroll');
  }

  closeMobileFilters() {
    this.mobileFiltersVisible = false;
    document.body.classList.remove('no-scroll');
  }

  loadNextPage() {
    this.nextPage.emit();
  }

  trackById(game: Game): number {
    return game.id;
  }
}
